import type { OperatingPoint } from "@/components/types";
import { type DataSet } from "@incytestudios/ddv";
import { type MaybeRef, type ComputedRef, unref, computed } from "vue";

export function useComputedDomains(
  dataSets: MaybeRef<DataSet[]>,
  xBufferMultiplier = 0, // percent (decimal) of the total range for the x values
  yBufferMutiplier = 0, // percent (decimal) of the total range for the y values
  operatingPoints?: MaybeRef<OperatingPoint[] | undefined>,
  canGoBelowZero = false
): ComputedRef<{ minX: number; maxX: number; minY: number; maxY: number }> {
  const minMaxes = computed(() => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const unwrapped = unref(dataSets);

    unwrapped?.forEach(dataset => {
      dataset.points.forEach(point => {
        minX = Math.min(minX, point.x as number);
        maxX = Math.max(maxX, point.x as number);
        minY = Math.min(minY, point.y as number);
        maxY = Math.max(maxY, point.y as number);
      });
    });

    const unwrappedOperatingPoints = unref(operatingPoints);

    unwrappedOperatingPoints?.forEach(({ point: op }) => {
      if (op.x < minX) {
        minX = op.x;
      }
      if (op.x > maxX) {
        maxX = op.x;
      }
      if (op.y < minY) {
        minY = op.y;
      }
      if (op.y > maxY) {
        maxY = op.y;
      }
    });

    return { minX, minY, maxX, maxY };
  });

  return computed(() => {
    const { minX, minY, maxX, maxY } = minMaxes.value;
    const totalY = maxY - minY;
    const totalX = maxX - minX;
    const domains = {
      minX: minX - Math.floor(totalX * xBufferMultiplier),
      maxX: maxX + Math.ceil(totalX * xBufferMultiplier),
      minY: minY - Math.floor(totalY * yBufferMutiplier),
      maxY: maxY + Math.ceil(totalY * yBufferMutiplier)
    };
    if (!canGoBelowZero) {
      domains.minX = Math.max(0, domains.minX);
      domains.minY = Math.max(0, domains.minY);
    }
    return domains;
  });
}
